<template>
  <div class="hotelux-date-range-calendar" v-click-outside="closePanel">
    <div class="calendar-trigger" @click="togglePanel">
      <font-icon
        class="icon-hotelux calendar-icon"
        icon="icon-hotelux-Calendar"
      />
      <UIText class="trigger-date" :type="isSticky ? '' : 'bold'">
        {{ dateText }}
      </UIText>
      <UIText v-if="showNightCount" class="nights-badge">
        {{ t("shared.hotel.search.night", nightCount) }}
      </UIText>
    </div>

    <div
      v-if="isPanelOpen"
      class="calendar-panel"
      :class="{ 'is-single': singleMonth }"
    >
      <div class="calendar-header">
        <button
          type="button"
          class="nav-button nav-prev"
          @click="handleMonthShift(-1)"
        >
          <svg class="nav-icon" viewBox="0 0 8 12">
            <path d="M7 1 1 6l6 5" />
          </svg>
        </button>

        <UIText
          v-for="month in visibleMonths"
          :key="month.valueOf()"
          class="month-title"
          type="bold"
        >
          {{ month.format("MMMM YYYY") }}
        </UIText>

        <button
          type="button"
          class="nav-button nav-next"
          @click="handleMonthShift(1)"
        >
          <svg class="nav-icon" viewBox="0 0 8 12">
            <path d="M1 1l6 5-6 5" />
          </svg>
        </button>
      </div>

      <div class="calendar-body">
        <div
          v-for="month in visibleMonths"
          :key="month.valueOf()"
          class="month-grid"
        >
          <UIText
            v-for="(weekday, index) in weekdays"
            :key="`weekday-${index}`"
            class="weekday-cell"
            type="bold"
          >
            {{ weekday }}
          </UIText>

          <template
            v-for="(day, index) in buildMonthDays(month)"
            :key="`day-${index}`"
          >
            <span v-if="!day" class="day-empty" />
            <button
              v-else
              type="button"
              class="day-cell"
              :class="dayClasses(day, index)"
              :disabled="isDisabled(day)"
              @click="handleDayClick(day)"
            >
              <UIText type="bold">{{ day.format("D") }}</UIText>
            </button>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import dayjs from "dayjs";
import { ClickOutside as vClickOutside, ElMessage } from "element-plus";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import UIText from "../UiText/index.vue";

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({})
  },
  showNightCount: {
    type: Boolean,
    default: true
  },
  singleMonth: {
    type: Boolean,
    default: false
  },
  // 吸顶态按设计稿回到常规字重
  isSticky: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(["update:modelValue", "change"]);

const { t } = useI18n();

const isPanelOpen = ref(false);
const leftMonth = ref(dayjs().startOf("month"));
const selectedCheckIn = ref(null);
const selectedCheckOut = ref(null);

// 设计稿星期为单字母缩写，取本地化缩写首字（en: S/M/T/W/T/F/S）
const weekdays = computed(() =>
  Array.from({ length: 7 }, (_, index) =>
    dayjs().day(index).format("dd").charAt(0)
  )
);

const visibleMonths = computed(() =>
  props.singleMonth
    ? [leftMonth.value]
    : [leftMonth.value, leftMonth.value.add(1, "month")]
);

const dateText = computed(() => {
  const { checkIn, checkOut } = props.modelValue || {};
  if (!checkIn || !checkOut) return "";
  return `${dayjs(checkIn).format("MMM DD")} - ${dayjs(checkOut).format(
    "MMM DD"
  )}`;
});

const nightCount = computed(() => {
  const { checkIn, checkOut } = props.modelValue || {};
  if (!checkIn || !checkOut) return 0;
  return dayjs(checkOut).diff(dayjs(checkIn), "day");
});

// 按月份实际周数渲染，避免出现整行空白
const buildMonthDays = month => {
  const firstDay = month.startOf("month");
  const days = Array.from({ length: firstDay.day() }, () => null);

  for (let date = 1; date <= month.daysInMonth(); date++) {
    days.push(firstDay.date(date));
  }

  return days;
};

const isDisabled = day => day.isBefore(dayjs(), "day");

const isSelected = day =>
  [selectedCheckIn.value, selectedCheckOut.value].some(
    selected => selected && day.isSame(selected, "day")
  );

const isInRange = day => {
  const checkIn = selectedCheckIn.value;
  const checkOut = selectedCheckOut.value;
  if (!checkIn || !checkOut) return false;
  return day.isAfter(checkIn, "day") && day.isBefore(checkOut, "day");
};

// 区间底色要盖住列间距才能连成整片，但首尾列不外扩，避免溢出月格
const dayClasses = (day, index) => {
  const inRange = isInRange(day);
  const column = index % 7;
  return {
    "is-selected": isSelected(day),
    "is-in-range": inRange,
    "connect-prev": inRange && column !== 0,
    "connect-next": inRange && column !== 6
  };
};

const openPanel = () => {
  const { checkIn, checkOut } = props.modelValue || {};
  selectedCheckIn.value = checkIn ? dayjs(checkIn) : null;
  selectedCheckOut.value = checkOut ? dayjs(checkOut) : null;
  leftMonth.value = (selectedCheckIn.value || dayjs()).startOf("month");
  isPanelOpen.value = true;
};

const closePanel = () => {
  isPanelOpen.value = false;
};

const togglePanel = () => {
  if (isPanelOpen.value) {
    closePanel();
    return;
  }
  openPanel();
};

const handleMonthShift = offset => {
  leftMonth.value = leftMonth.value.add(offset, "month");
};

const handleDayClick = day => {
  if (!selectedCheckIn.value || selectedCheckOut.value) {
    selectedCheckIn.value = day;
    selectedCheckOut.value = null;
    return;
  }

  if (day.isSame(selectedCheckIn.value, "day")) {
    ElMessage.warning(t("shared.hotel.search.dateNotSame"));
    return;
  }

  if (day.isBefore(selectedCheckIn.value, "day")) {
    selectedCheckIn.value = day;
    return;
  }

  selectedCheckOut.value = day;
  const dateRange = [
    selectedCheckIn.value.toDate(),
    selectedCheckOut.value.toDate()
  ];
  emit("update:modelValue", { checkIn: dateRange[0], checkOut: dateRange[1] });
  emit("change", dateRange);
  closePanel();
};

defineOptions({
  name: "HoteluxDateRangeCalendar"
});
</script>

<style lang="scss" scoped>
.hotelux-date-range-calendar {
  display: flex;

  .calendar-trigger {
    display: flex;
    align-items: center;
    cursor: pointer;
  }

  .calendar-icon {
    margin-right: 12px;
    font-size: 20px;
    color: #2a2c35;
  }

  .trigger-date {
    // font-family: "lato-bold";
    font-size: 16px;
    color: #212223;
    white-space: nowrap;
  }

  .nights-badge {
    height: 24px;
    padding: 0 8px;
    margin-left: 12px;
    // font-family: lato;
    font-size: 14px;
    line-height: 24px;
    color: #212223;
    background: #e9e9e9;
  }

  // 双月面板按 960 设计稿相对搜索栏居中；单月面板与搜索栏同宽
  .calendar-panel {
    position: absolute;
    // 17px = 16px 间距 + 搜索栏 1px 下描边
    top: calc(100% + 17px);
    left: 50%;
    width: 960px;
    margin-left: -480px;
    padding: 32px 0 40px;
    background: #fff;
    box-shadow: 0 10px 28px rgb(0 0 0 / 11%);

    &.is-single {
      right: 0;
      left: 0;
      width: auto;
      margin-left: 0;
    }
  }

  .calendar-header {
    position: relative;
    display: flex;
    align-items: center;
    gap: 48px;
    height: 40px;
    margin-bottom: 21px;
    padding: 0 72px;
  }

  .month-title {
    flex: 1;
    // font-family: "lato-bold";
    font-size: 20px;
    line-height: 24px;
    color: #212223;
    text-align: center;
  }

  .nav-button {
    position: absolute;
    top: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    padding: 0;
    background: transparent;
    border: 0;
    transform: translateY(-50%);
    cursor: pointer;

    &.nav-prev {
      left: 40px;
    }

    &.nav-next {
      right: 40px;
    }
  }

  .nav-icon {
    width: 8px;
    height: 12px;
    fill: none;
    stroke: #2a2c35;
    stroke-width: 2;
  }

  .calendar-body {
    display: flex;
    justify-content: center;
    gap: 48px;
    padding: 0 72px;
  }

  .month-grid {
    display: grid;
    grid-template-columns: repeat(7, 48px);
    gap: 12px 8px;
  }

  .weekday-cell,
  .day-cell {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    // font-family: lato-bold;
    font-size: 16px;
  }

  .day-empty {
    display: block;
    width: 48px;
    height: 48px;
  }

  .weekday-cell {
    color: #acacac;
  }

  .day-cell {
    padding: 0;
    color: #212223;
    background: transparent;
    border: 0;
    cursor: pointer;

    &:disabled {
      color: #cfcfcf;
      cursor: default;
    }

    &:not(:disabled):not(.is-selected):not(.is-in-range):hover {
      background: #f5f5f5;
    }

    &.is-in-range {
      // 设计稿区间底色
      background: #f4f6f8;

      // 用阴影补上左右列间距，让区间连成一片
      &.connect-prev {
        box-shadow: -8px 0 0 #f4f6f8;
      }

      &.connect-next {
        box-shadow: 8px 0 0 #f4f6f8;
      }

      &.connect-prev.connect-next {
        box-shadow: -8px 0 0 #f4f6f8, 8px 0 0 #f4f6f8;
      }
    }

    &.is-selected {
      color: #fff;
      background: #2a2c35;
    }
  }
}
</style>
